<script setup lang="ts">
import { nextTick, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useLocale } from '@/plugins/translation';
import SelectWithSearch from '@/components/my-students/SelectWithSearch.vue';
import { useCustomerMgmt } from '@ilteducation/customer-mgmt';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

declare type UserHeader = {
  readonly value?: string;
  readonly title?: string | undefined;
  readonly align?: 'start' | 'end' | 'center' | undefined;
  readonly width?: string | number | undefined;
  readonly minWidth?: string | undefined;
  readonly maxWidth?: string | undefined;
  readonly sortable?: boolean | undefined;
};

declare type SchoolUnit = {
  uid: string;
  legacyId: string;
  name: string;
  active: boolean;
};

declare type Group = {
  uid: string;
  legacyId: string;
  name: string;
  code: string | null;
  externalId: string | null;
  memberCount: number;
  sourceSystem: {
    name: string;
    type: string;
  };
  schoolUnit: SchoolUnit;
};

// The order does matter, in case of changing order sorting will be required.
const defaultOptionsUsers = {
  page: 1,
  itemsPerPage: 15,
  sortBy: ['name'],
  sortDesc: [false],
  groupBy: [],
  groupDesc: [false],
  mustSort: false,
  multiSort: false,
  searchString: '',
  filters: {},
  force: false,
};
const defaultOptionsGroups = {
  page: 1,
  itemsPerPage: 10,
  sortBy: ['id'],
  sortDesc: [false],
  groupBy: ['id'],
  groupDesc: [false],
  mustSort: false,
  multiSort: false,
  searchString: '',
  filters: {},
  force: false,
};

const router = useRouter();
const { t, l } = useLocale();
const headers: UserHeader[] = [
  {
    value: 'name',
    title: 'my_page.my_students_tab.student',
    width: '65%',
    align: 'start',
    sortable: false,
  },
  {
    value: 'group',
    title: 'my_page.my_students_tab.group',
    width: '35%',
    align: 'start',
    sortable: false,
  },
];

const {
  displayedItems: schoolUnits,
  selectedItems: selectedSchoolUnits,
  setSelectedItems: setSelectedSchoolUnits,
  fetchSearch: fetchSearchSchoolUnits,
} = useCustomerMgmt({ context: 'schoolUnits' });

const {
  fetchParams: fetchParamsGroups,
  setFetchParams: setFetchParamsGroups,
  resetToDefaults: resetToDefaultsGroups,
} = useCustomerMgmt({ context: 'groups' });

const {
  fetchParams: fetchParamsUsers,
  displayedItems: users,
  isFetching: isFetchingUsers,
  totalItems: totalUsers,
  searchField: searchFieldUsers,
  setSearchField: setSearchFieldUsers,
  setFetchParams: setFetchParamsUsers,
  cleanAndFetch: cleanAndFetchUsers,
  fetchSearch: fetchSearchUsers,
  resetToDefaults: resetToDefaultsUsers,
} = useCustomerMgmt({ context: 'users', options: defaultOptionsUsers });

const currentOrganization = computed({
  get: () => selectedSchoolUnits.value,
  set: async (newOptions) => {
    if (JSON.stringify(newOptions) !== JSON.stringify(selectedSchoolUnits.value)) {
      setSelectedSchoolUnits(newOptions);
    }
  },
});

const optionsGroups = computed({
  get: () => fetchParamsGroups.value,
  set: async (newOptions) => {
    await nextTick();

    if (JSON.stringify(newOptions) !== JSON.stringify(fetchParamsGroups.value)) {
      setFetchParamsGroups(newOptions);
      resetToDefaultsGroups();
    }
  },
});

const optionsUsers = computed({
  get: () => fetchParamsUsers.value,
  set: async (newOptions) => {
    await nextTick();

    if (JSON.stringify(newOptions) === JSON.stringify(defaultOptionsUsers)) {
      setFetchParamsUsers(newOptions);
      resetToDefaultsUsers();
    } else if (JSON.stringify(newOptions) !== JSON.stringify(fetchParamsUsers.value)) {
      setFetchParamsUsers(newOptions);

      if (fetchParamsUsers.value.searchString !== newOptions.searchString) {
        await fetchSearchUsers();
      } else {
        await cleanAndFetchUsers();
      }
    }
  },
});

const searchValue = computed({
  get: () => searchFieldUsers.value,
  set: (value) => {
    if (value !== undefined) {
      setSearchFieldUsers(value);
    }
  },
});

const onOrganizationSelect = (selectedOrganization: any | null) => {
  const selectedOrganizationUid = selectedOrganization?.uid ?? null;
  if (selectedOrganizationUid === null) {
    optionsUsers.value = defaultOptionsUsers;
    optionsGroups.value = defaultOptionsGroups;
  } else {
    const filtersUsers = { ...optionsUsers.value.filters, ...{ schoolUnits: [selectedOrganizationUid] } };
    const filtersGroups = { ...optionsGroups.value.filters, ...{ schoolUnits: [selectedOrganizationUid] } };

    optionsUsers.value = { ...optionsUsers.value, ...{ filters: filtersUsers, page: 1 } };
    optionsGroups.value = { ...optionsGroups.value, ...{ filters: filtersGroups, page: 1 } };
  }
};

const onGroupChanged = (selectedGroups: Group[] | Group | null) => {
  let filters;

  if (selectedGroups !== null) {
    const groupsFilter = Array.isArray(selectedGroups)
      ? selectedGroups.map((group) => group.uid)
      : [selectedGroups.uid];

    filters = { ...optionsUsers.value.filters, ...{ groups: groupsFilter } };
  } else {
    const { ...cleanedFilters } = optionsUsers.value.filters;
    delete cleanedFilters.groups;
    filters = cleanedFilters;
  }
  optionsUsers.value = { ...optionsUsers.value, ...{ filters, page: 1 } };
};

const searchUsers = () => {
  const name = searchValue.value;
  const filters = { ...optionsUsers.value.filters, ...{ name } };

  optionsUsers.value = { ...optionsUsers.value, ...{ filters, page: 1 } };
};

const onClear = async () => {
  await nextTick();
  const name = searchValue.value;
  const filters = { ...optionsUsers.value.filters, ...{ name } };

  optionsUsers.value = { ...optionsUsers.value, ...{ filters, page: 1 } };
};

const openStudentPage = (userUid: string) => {
  const routerParams = {
    name: 'users.person',
    params: {
      userUid,
    },
  };
  router.push(routerParams);
};

const loadItems = (args: any) => {
  const { page } = args;
  optionsUsers.value = { ...optionsUsers.value, ...{ page } };
};

onMounted(async () => {
  if (schoolUnits.value.length === 0) {
    await fetchSearchSchoolUnits();
  }

  if (
    Array.isArray(currentOrganization.value) &&
    currentOrganization.value.length === 0 &&
    schoolUnits.value.length === 1
  ) {
    currentOrganization.value = schoolUnits.value[0].uid;
    onOrganizationSelect(schoolUnits.value[0].uid);
  }
});
</script>

<template>
  <v-container class="mx-0">
    <v-row>
      <v-col cols="12" sm="6">
        <SelectWithSearch
          context="schoolUnits"
          :label="t(l.my_page.my_students_tab.organization, 1)"
          clearable
          @change-select-value="onOrganizationSelect"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <SelectWithSearch
          context="groups"
          :label="t(l.my_page.my_students_tab.group, 1)"
          :disabled="currentOrganization === null || currentOrganization.length === 0"
          clearable
          @change-select-value="onGroupChanged"
        />
      </v-col>
    </v-row>
    <v-row class="heading-row">
      <v-col cols="12">
        <h2 class="h3">{{ t(l.my_page.my_students_tab.page_title) }}</h2>
        <p>
          <FontAwesomeIcon icon="fa-solid fa-user" /> {{ totalUsers }}
          {{ t(l.my_page.my_students_tab.student, totalUsers) }}
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-data-table-server
          class="data-table rounded-sm"
          :headers="headers"
          :loading="isFetchingUsers"
          :items-length="totalUsers && totalUsers > -1 ? totalUsers : (optionsUsers?.page || 1) * users?.length + 1"
          :items="users"
          :search="optionsUsers.searchString"
          :items-per-page="optionsUsers.itemsPerPage"
          :items-per-page-options="[]"
          :multi-sort="false"
          :no-data-text="t(l.my_page.no_data)"
          :header-props="{ scope: 'col' }"
          @update:options="loadItems"
        >
          <template #top>
            <div class="mb-5">
              <v-text-field
                v-model="searchValue"
                :label="t(l.my_page.my_students_tab.search_placeholder)"
                single-line
                hide-details
                clearable
                density="compact"
                variant="outlined"
                rounded="md"
                flat
                class="search-field"
                @click:clear="onClear"
                @keyup.enter="searchUsers"
              >
                <template #prepend-inner>
                  <FontAwesomeIcon icon="fa-regular fa-magnifying-glass"></FontAwesomeIcon>
                </template>
              </v-text-field>
            </div>
          </template>
          <template #[`header.name`]="{ column }">{{ t(column.title!, 2) }}</template>
          <template #[`header.group`]="{ column }">{{ t(column.title!, 1) }}</template>
          <template #item="{ item }">
            <tr @click="openStudentPage(item.uid)">
              <td>{{ item.name }}</td>
              <td>{{ item.groups.join(', ') }}</td>
            </tr>
          </template>
        </v-data-table-server>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
:deep(.v-table__wrapper) {
  tr:hover {
    cursor: pointer;
  }
  thead {
    th {
      font-size: 1.125rem !important;
      font-weight: 700 !important;
      background-color: rgb(var(--v-theme-secondary));
      color: white !important;
    }
  }
  td {
    font-size: 1.125rem !important;
  }
}

:deep(.v-data-table-footer__items-per-page) {
  display: none;
}

.heading-row {
  color: rgb(var(--v-theme-on-surface-medium));
}
</style>
